// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { useEffect, useState } from 'react';

const INTAKE_QUESTIONNAIRE_DEFERRED_URL =
  'http://polarissleep.com/fhir/StructureDefinition/intake-questionnaire-deferred';
const TAG_SYSTEM = 'http://polarissleep.com/tags';

/**
 * Phases a patient with a login can be parked at before finishing the questionnaire: after
 * booking, or partway through it after choosing to fill it out now, which advances the
 * phase without setting the deferred extension.
 */
const OUTSTANDING_INTAKE_PHASES = new Set([
  'eligibility-appointment-booked',
  'eligibility-check-page-1',
  'sleep-history',
]);

/**
 * A patient still owes the sleep-history questionnaire while either signal holds:
 * they are parked at a pre-finished phase, or they explicitly deferred it. The
 * intake bot strips the deferred extension once the patient reaches
 * `eligibility-check-finished`, so this goes false on its own when they finish.
 */
function isOutstanding(patient: Patient): boolean {
  const parkedInIntake = Boolean(
    patient.meta?.tag?.some((tag) => tag.system === TAG_SYSTEM && OUTSTANDING_INTAKE_PHASES.has(tag.code ?? ''))
  );
  const deferred = Boolean(patient.extension?.some((extension) => extension.url === INTAKE_QUESTIONNAIRE_DEFERRED_URL));
  return parkedInIntake || deferred;
}

export interface IntakeQuestionnaireOutstandingResult {
  outstanding: boolean;
  loading: boolean;
}

/**
 * Whether the signed-in patient still has an intake sleep-history questionnaire to
 * complete. Re-reads the Patient rather than trusting the cached profile, because the
 * deferred extension is written by a bot after the profile was first loaded.
 */
export function useIntakeQuestionnaireOutstanding(): IntakeQuestionnaireOutstandingResult {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;
  const [outstanding, setOutstanding] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.id) {
      setOutstanding(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    medplum
      .readResource('Patient', profile.id)
      .then((patient) => {
        if (!cancelled) {
          setOutstanding(isOutstanding(patient));
        }
      })
      .catch(() => {
        // Fall back to the cached profile so a read failure never hides the prompt.
        if (!cancelled) {
          setOutstanding(isOutstanding(profile));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, profile]);

  return { outstanding, loading };
}
